import { CancellationToken } from "../../../base/common/cancellation";
import { Event } from "../../../base/common/events";
import { IJSONSchema } from "../../../base/common/jsonSchema";
import { createDecorator } from "../../instantiation/common/registry";


// #region AI Service

export type AIPersona = "chat" | ({} & string);

export const IAIService = createDecorator<IAIService>("aiService");

export type AIServiceChangedEvent = {
    readonly service: IAIService;
}
export interface IAIService {

    readonly onDidChange: Event<AIServiceChangedEvent>;

    readonly contextService: IAIContextService;
    readonly toolService: IAIToolService;

    /**
     * Get an AI system provider for the given persona.
     */
    getProvider(persona: AIPersona): IAISystemProvider | null;

    /**
     * Activate the given AI system provider.
     */
    registerProvider(provider: IAISystemProvider): Promise<void>;

}



export const enum AISystemProviderCapabilities {

    None = 0,

    TextGeneration = 1 << 0,
    TextGenerationStreaming = 1 << 1,

    ChatHistory = 1 << 2,
    // ContextAwareness = 1 << 3,

    // ImageGeneration = 1 << 2,
    // ImageGenerationVariations = 1 << 3,

}



// #region AI System Provider

export interface AIChatMessage {
    role: "user" | "assistant" | "system";
    content: string;
    timestamp: number;

    reasoning?: string,
    refusal?: string | null,
    tool_calls?: Array<{
        toolName: string;
        arguments?: string,
        result?: string,
    }>;

    /**
     * This may be set to mark a chat message be generated by a tool call. It can be used to hide messages but keep them in history.
     */
    type?: "tool_response";
}

export interface AiSystemProviderDidChangeEvent {
    readonly provider: IAISystemProvider;
    finished?: boolean;
    error?: string;
}
export interface AISystemProviderCapabilitiesChangedEvent {
    readonly provider: IAISystemProvider;
}

export type GenerateOptions = {
    systemPrompt?: string;
    token?: CancellationToken;
}

export interface IAISystemProvider {

    readonly persona: AIPersona;
    capabilities: AISystemProviderCapabilities;

    readonly onDidChange: Event<AiSystemProviderDidChangeEvent>;
    readonly onCapabilitiesChanged: Event<AISystemProviderCapabilitiesChangedEvent>;

    get isProcessing(): boolean;

    canGenerateText?(): Promise<boolean>;
    generateText?(prompt: string, options?: GenerateOptions): Promise<AIGenerateTextResponse>;
    generateTextStream?(prompt: string, options?: GenerateOptions): AsyncIterable<AIGenerateTextResponse>;

    getChatHistory?(): Promise<AIChatMessage[]>;
    clearChatHistory?(): Promise<void>;

    // generateImage?(prompt: string, options?: { width?: number; height?: number; count?: number }): Promise<string[]>;
}

export type AIGenerateTextResponse = {
    text: string;
}

export interface IAISystemProviderWithTextGeneration extends IAISystemProvider {
    canGenerateText(): Promise<boolean>;
    generateText(prompt: string, options?: GenerateOptions): Promise<AIGenerateTextResponse>;
}
export function isAISystemProviderWithTextGeneration(provider: IAISystemProvider): provider is IAISystemProviderWithTextGeneration {
    return !!(provider.capabilities & AISystemProviderCapabilities.TextGeneration)
};

export interface IAISystemProviderWithTextGenerationStreaming extends IAISystemProvider {
    canGenerateText(): Promise<boolean>;
    generateTextStream(prompt: string, options?: GenerateOptions): AsyncIterable<AIGenerateTextResponse>;
}
export function isAISystemProviderWithTextGenerationStreaming(provider: IAISystemProvider): provider is IAISystemProviderWithTextGenerationStreaming {
    return !!(provider.capabilities & AISystemProviderCapabilities.TextGenerationStreaming)
}

export interface IAISystemProviderWithChatHistory extends IAISystemProvider {
    getChatHistory(): Promise<Array<AIChatMessage>>;
    clearChatHistory(): Promise<void>;
}
export function isAISystemProviderWithChatHistory(provider: IAISystemProvider): provider is IAISystemProviderWithChatHistory {
    return !!(provider.capabilities & AISystemProviderCapabilities.ChatHistory)
}

// export interface IAISystemProviderWithContextAwareness extends IAISystemProvider {
//     // marker interface
// }
// export function isAISystemProviderWithContextAwareness(provider: IAISystemProvider): provider is IAISystemProviderWithContextAwareness {
//     return !!(provider.capabilities & AISystemProviderCapabilities.ContextAwareness)
// }










// #region AI Context


export const IAIContextService = createDecorator<IAIContextService>('IAIContextService');


export interface AIContextChangedEvent {
    provider: IAIContextProvider;
}

/**
 * Service to manage AI context providers
 */
export interface IAIContextService {

    readonly onDidChange: Event<AIContextChangedEvent>;

    registerProvider(provider: IAIContextProvider): void;
    getProviders(): Promise<Array<IAIContextProvider> | null>;


}

export enum AIContextProviderCapabilities {
    None = 0,
    Text = 1 << 0,
    Image = 1 << 1,
    File = 1 << 2,
}

/**
 * The AI context provider is meant to provide additional context to AI systems,
 * e.g. content of the currently viewed file or website, information about currently opened files (scripts, 3D models, etc.) or perhaps error messages
 */
export interface IAIContextProvider {

    capabilities: AIContextProviderCapabilities;

    getContext(): Promise<AITextContextChunk[] | null>;

}

/**
 * A chunk of text context provided by an AI context provider
 */
export type AITextContextChunk = {
    type: 'text',
    text: string
} | {
    type: "image",
    data: Uint8Array | string,
} | {
    type: "file",
    name: string,
    path: string,
    size: number,
}


// #endregion

// #region AI tools

export const IAIToolService = createDecorator<IAIToolService>('IAIToolService');

export interface AIToolServiceChangedEvent {
    readonly service: IAIToolService;
}
export interface IAIToolService {

    readonly onDidChange: Event<AIToolServiceChangedEvent>;

    registerTool(tool: IAITool): void;
    getAvailableTools(): Promise<IAITool[]>;
    callTool(name: string, parameters: object): Promise<AIToolCallResponse>;
}

export interface AIToolCallResponse {
    result: {
        success: boolean;
        message?: string;
        /**
         * Optional AI message content returned by the tool. When present, this message can be directly used as a response to the AI user instead of further processing the tool response.
         */
        aiMessage?: string;
        // aiImages?: string[];
    }
}
export function isAIToolCallResponse(obj: any): obj is AIToolCallResponse {
    return obj && typeof obj === "object" && "result" in obj && typeof obj.result === "object" && "success" in obj.result;
}

export interface IAITool<T extends unknown = unknown> {
    readonly type: "function";

    name: string;
    description: string;
    parametersJSONSchema: IJSONSchema;
    allowMcp?: boolean;

    // canRun?(): Promise<boolean>;
    call(parameters?: T): Promise<AIToolCallResponse>;

    canRun?(): boolean;
}

export abstract class FunctionAiTool<T extends unknown = unknown> implements IAITool<T> {
    readonly type = "function";
    abstract name: string;
    abstract description: string;
    abstract parametersJSONSchema: IJSONSchema;
    readonly allowMcp?: boolean;
    abstract call(parameters?: T): Promise<AIToolCallResponse>;
    canRun?(): boolean;
}